import React, { useState, useEffect, useRef } from 'react';

import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import { setUploadErrors } from 'Helpers/errors';

import Button from 'CommonComponents/Button';
import TextButton from 'CommonComponents/TextButton';

import EditIcon from 'Assets/icons/edit.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const GeneralSupplierEditSchema = Yup.object().shape({
    name: Yup.string().required($t('This value is required')),
    type: Yup.string().required($t('This value is required')),
    location: Yup.string().required($t('This value is required')),
    area_operation: Yup.string(),
    services_description: Yup.string(),
    key_contact: Yup.string(),
    website: Yup.string(),
    contact_details: Yup.string(),
});

export default function SupplierFrameworkTableRow(props) {
    const fileRef = useRef();

    const [edit, setEdit] = useState(false);
    const [formDisabled, setFormDisabled] = useState(false);
    const [request, setRequest] = useState(false);
    const [attachments, setAttachments] = useState([]);

    const cancelEdit = () => {
        setEdit(false);
    };

    useEffect(() => {
        setAttachments(JSON.parse(JSON.stringify(props.supplier.attachments)));
    }, [props.supplier]);

    return (
        <>
            {!edit ? (
                <tr>
                    <td>{props.supplier.name}</td>
                    <td>{props.supplier.type}</td>
                    <td>{props.supplier.location}</td>
                    <td>{props.supplier.area_operation}</td>
                    <td>{props.supplier.services_description}</td>
                    <td>{props.supplier.key_contact}</td>
                    <td>{props.supplier.website}</td>
                    <td>{props.supplier.contact_details}</td>
                    <td>
                        <ul>
                            {props.supplier.attachments.map((attachment) => {
                                return (
                                    <li key={`attachment_${attachment.id}`}>
                                        <a onClick={(e) => props.download(e, attachment)}>{attachment.fileName}</a>
                                    </li>
                                );
                            })}
                        </ul>
                    </td>
                    <td className="tw-align-middle">
                        {props.canEdit && (
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                <TextButton onClick={() => setEdit(true)}>
                                    <EditIcon className="tw-w-3 tw-h-3" />
                                    {$t('Edit')}
                                </TextButton>
                                <TextButton onClick={() => props.deleteSupplier(props.supplier)}>
                                    <TrashIcon className="tw-w-3 tw-h-3" />
                                    {$t('Delete')}
                                </TextButton>
                            </div>
                        )}
                    </td>
                </tr>
            ) : (
                <tr className={'cursor-pointer bg-grey'}>
                    <td colSpan={10}>
                        {props.canEdit && (
                            <Formik
                                initialValues={{
                                    name: props.supplier.name || '',
                                    type: props.supplier.type || '',
                                    location: props.supplier.location || '',
                                    area_operation: props.supplier.area_operation || '',
                                    services_description: props.supplier.services_description || '',
                                    key_contact: props.supplier.key_contact || '',
                                    website: props.supplier.website || '',
                                    contact_details: props.supplier.contact_details || '',
                                    files: [],
                                }}
                                validationSchema={GeneralSupplierEditSchema}
                                onSubmit={(values, actions) => {
                                    setRequest(true);

                                    const formData = new FormData();
                                    Object.keys(values).forEach((key) => {
                                        if (key === 'files') {
                                            Array.from(values[key]).forEach((file) => {
                                                formData.append('files[]', file);
                                            });
                                        } else {
                                            formData.append(key, values[key]);
                                        }
                                    });

                                    axios({
                                        method: 'post',
                                        url: props.updateUrl,
                                        data: formData,
                                        headers: { 'Content-Type': 'multipart/form-data' },
                                    })
                                        .then((response) => {
                                            props.fetchData(() => {
                                                setEdit(false);
                                            });
                                        })
                                        .catch((e) => {
                                            setUploadErrors(e, 'files.0', 'files', actions);
                                        })
                                        .finally(() => {
                                            actions.setSubmitting(false);
                                            setRequest(false);
                                        });
                                }}
                            >
                                {({ values, errors, touched, isSubmitting, handleSubmit, setFieldValue }) => (
                                    <>
                                        <div className="row text-left">
                                            <div className="col-2 text-blue">
                                                {$t('Supplier Name')} <span className="text-danger">*</span>
                                            </div>
                                            <div className="col-4">
                                                <div className="form-group">
                                                    <Field
                                                        type="text"
                                                        className={
                                                            errors.name && touched.name ? 'form-control is-invalid border border-danger' : 'form-control'
                                                        }
                                                        name="name"
                                                        placeholder={$t('Enter supplier name')}
                                                    />
                                                    {errors.name && touched.name && <div className="text-danger">{errors.name}</div>}
                                                </div>
                                            </div>

                                            <div className="col-2 text-blue">
                                                {$t('Type')} <span className="text-danger">*</span>
                                            </div>
                                            <div className="col-4">
                                                <div className="form-group">
                                                    <Field
                                                        type="text"
                                                        className={
                                                            errors.type && touched.type ? 'form-control is-invalid border border-danger' : 'form-control'
                                                        }
                                                        name="type"
                                                        placeholder={$t('Enter type')}
                                                    />
                                                    {errors.type && touched.type && <div className="text-danger">{errors.type}</div>}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row text-left">
                                            <div className="col-2 text-blue">
                                                {$t('Office Location')} <span className="text-danger">*</span>
                                            </div>
                                            <div className="col-4">
                                                <div className="form-group">
                                                    <Field
                                                        as={'textarea'}
                                                        type="text"
                                                        className={
                                                            errors.location && touched.location
                                                                ? 'form-control is-invalid border border-danger'
                                                                : 'form-control'
                                                        }
                                                        name="location"
                                                        placeholder={$t('Enter Office Location')}
                                                    />
                                                    {errors.location && touched.location && <div className="text-danger">{errors.location}</div>}
                                                </div>
                                            </div>

                                            <div className="col-2 text-blue">{$t('Area of Operation')}</div>
                                            <div className="col-4">
                                                <div className="form-group">
                                                    <Field
                                                        as={'textarea'}
                                                        type="text"
                                                        className={
                                                            errors.area_operation && touched.area_operation
                                                                ? 'form-control is-invalid border border-danger'
                                                                : 'form-control'
                                                        }
                                                        name="area_operation"
                                                        placeholder={$t('Enter Area of Operation')}
                                                    />
                                                    {errors.area_operation && touched.area_operation && (
                                                        <div className="text-danger">{errors.area_operation}</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="row text-left">
                                            <div className="col-2 text-blue">{$t('Key Contact')}</div>
                                            <div className="col-4">
                                                <Field
                                                    type="text"
                                                    className={
                                                        errors.key_contact && touched.key_contact
                                                            ? 'form-control is-invalid border border-danger'
                                                            : 'form-control'
                                                    }
                                                    name="key_contact"
                                                    placeholder={$t('Enter Key Contact')}
                                                />
                                                {errors.key_contact && touched.key_contact && <div className="text-danger">{errors.key_contact}</div>}
                                            </div>

                                            <div className="col-2 text-blue">{$t('Website')}</div>
                                            <div className="col-4">
                                                <Field
                                                    type="text"
                                                    className={
                                                        errors.website && touched.website ? 'form-control is-invalid border border-danger' : 'form-control'
                                                    }
                                                    name="website"
                                                    placeholder={$t('Enter Website')}
                                                />
                                                {errors.website && touched.website && <div className="text-danger">{errors.website}</div>}
                                            </div>
                                        </div>

                                        <div className="row my-2 text-left">
                                            <div className="col-2 text-blue">{$t('Description of services')}</div>
                                            <div className="col-4">
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.services_description && touched.services_description
                                                            ? 'form-control is-invalid border border-danger'
                                                            : 'form-control'
                                                    }
                                                    name="services_description"
                                                    placeholder={$t('Enter Description of services')}
                                                />
                                                {errors.services_description && touched.services_description && (
                                                    <div className="text-danger">{errors.services_description}</div>
                                                )}
                                            </div>

                                            <div className="col-2 text-blue">{$t('Contact Details')}</div>
                                            <div className="col-4">
                                                <Field
                                                    as={'textarea'}
                                                    type="text"
                                                    className={
                                                        errors.contact_details && touched.contact_details
                                                            ? 'form-control is-invalid border border-danger'
                                                            : 'form-control'
                                                    }
                                                    name="contact_details"
                                                    placeholder={$t('Enter Contact Details')}
                                                />
                                                {errors.contact_details && touched.contact_details && (
                                                    <div className="text-danger">{errors.contact_details}</div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row my-2 text-left">
                                            <div className="col-2 text-blue">{$t('Add Attachments')}</div>
                                            <div className="col-8">
                                                <input
                                                    ref={fileRef}
                                                    type="file"
                                                    className={errors.files && touched.files ? 'form-control is-invalid border border-danger' : 'form-control'}
                                                    name="files"
                                                    multiple
                                                    placeholder="Select Files"
                                                    accept={ACCEPTED_ATTACHMENT_FILE_TYPES}
                                                    onChange={(event) => {
                                                        setFieldValue('files', event.currentTarget.files);
                                                    }}
                                                />
                                                {errors.files && touched.files && <div className="text-danger">{errors.files}</div>}
                                            </div>
                                            {request && (
                                                <div className="col-2">
                                                    <div className="col-12 d-flex align-items-center justify-content-center text-blue">
                                                        <div
                                                            className="spinner-border"
                                                            style={{
                                                                width: '2rem',
                                                                height: '2rem',
                                                            }}
                                                            role="status"
                                                        >
                                                            <span className="sr-only">{$t('Loading')}...</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className={'col d-flex justify-content-end'}>
                                            <Button variant="secondary" className="mx-1" onClick={() => cancelEdit()}>
                                                {$t('Cancel')}
                                            </Button>
                                            <Button variant={formDisabled ? 'secondary' : 'primary'} className="mx-1" onClick={handleSubmit} disabled={request}>
                                                {$t('Save')}
                                            </Button>
                                        </div>

                                        {attachments.length > 0 && (
                                            <div className="row my-2 text-left">
                                                {attachments.map((attachment) => {
                                                    return (
                                                        <div className="col-4" key={`attachment-${attachment.id}`}>
                                                            <div className={'card bg-white border-0 rounded-15px py-2 shadow'}>
                                                                <div className={'card-body'}>
                                                                    <div className="row">
                                                                        <div className="col-9">
                                                                            <a href={''} onClick={(e) => props.download(e, attachment)}>
                                                                                {attachment.fileName}
                                                                            </a>
                                                                        </div>
                                                                        <div className="col-3">
                                                                            <small className={'d-block'}>
                                                                                <Button
                                                                                    small
                                                                                    variant="danger"
                                                                                    type="button"
                                                                                    onClick={() => props.deleteAttachment(attachment)}
                                                                                >
                                                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-mr-2" />
                                                                                    {$t('Delete')}
                                                                                </Button>
                                                                            </small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                )}
                            </Formik>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}
